import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/*
Test cases:
1. Navigate to a next/previous page
  - check if the time is added after response from the server
  - check if the time is not added to previous time
2. Different instances of the same page (navigation home -> page -> home -> page)
  – check if the time is not added to the wrong instance
3. Close the browser tab and open it again (or refresh the page)
  – check if the time is not added when the tab is not active
4. Switch between tabs 
  – check if the time is not added when the tab is not active
 */

interface TimeSpentStore {
  sessions: Record<string, {
    sessionStartTime: number;
    cumulativeTime: number;
    lastActivity: number;
    currentPageId: string | null;
    isActive: boolean;
  }>;
  currentInstanceId: string | null;
  getCurrentInstanceId: () => string | null;
  setCurrentInstanceId: (id: string) => void;
  setCurrentPageId: (id: string) => void;
  getCurrentPageId: () => string | null
  addSessionTime: (id?: string) => void;
  pauseSession: (id?: string) => void;
  resumeSession: (id?: string) => void
  getTimeSpent: (id?: string) => number;
  resetStartTime: (id?: string) => void;
  updateCurrentActivity: () => void;
  expireSessions: () => void;
}

const SESSION_TIMEOUT = 15 * 24 * 60 * 60 * 1000; // 15 days

export const useTimeSpentStore = create<TimeSpentStore>()(
  persist(
    (set, get) => ({
        // Object with session data for each instance
        sessions: {},
        // Current product instance ID
        currentInstanceId: null,
        // Get current instance ID
        getCurrentInstanceId: () => get().currentInstanceId,
        // Set current instance ID
        setCurrentInstanceId: (instanceId: string) => {
          const now = Date.now();
          const { currentInstanceId, sessions } = get();

          // Update session time for the previous product if we switch from another instance
          if (currentInstanceId && currentInstanceId !== instanceId) {
            get().pauseSession(currentInstanceId);
          }

          // Get session data for the new product
          let productSession = sessions[instanceId];

          // Create new session if it doesn't exist or last activity was more than 15 days ago
          if (!productSession || now - productSession.lastActivity > SESSION_TIMEOUT) {
            productSession = {
              sessionStartTime: now,
              cumulativeTime: 0,
              lastActivity: now,
              isActive: true,
              currentPageId: null,
            };
          } else {
            // Update last activity time if session exists
            if (!productSession.isActive) {
              // Якщо була пауза, відновлюємо сесію (без додавання часу за період, коли була невидима)
              productSession.sessionStartTime = now;
              productSession.isActive = true;
            }
            productSession.lastActivity = now;
          }

          set((state) => ({
            currentInstanceId: instanceId,
            sessions: { ...state.sessions, [instanceId]: productSession },
          }));
        },
        // Set current page ID for the current instance
        setCurrentPageId: (pageId: string) => {
          const { currentInstanceId, sessions } = get();
          if (currentInstanceId && sessions[currentInstanceId]) {
            const session = sessions[currentInstanceId];
            session.currentPageId = pageId;
            set((state) => ({
              sessions: { ...state.sessions, [currentInstanceId]: session },
            }));
          }
        },
        // Get current page ID for the current instance
        getCurrentPageId: () => {
          const { currentInstanceId, sessions } = get();
          if (currentInstanceId && sessions[currentInstanceId]) {
            return sessions[currentInstanceId].currentPageId;
          }
          return null;
        },
        // Update current session – add time from sessionStartTime to cumulativeTime
        addSessionTime: (instanceId?: string) => {
          const now = Date.now();

          const id = instanceId || get().currentInstanceId;
          if (!id) return;

          const { sessions } = get();
          let session = sessions[id];

          if (session && session.isActive) {
            const sessionTime = now - session.sessionStartTime;
            session.cumulativeTime += sessionTime;
            // Reset session start time
            session.sessionStartTime = now;
            session.lastActivity = now;
            set((state) => ({
              sessions: { ...state.sessions, [id]: session },
            }));
          }
        },
        // Pause session – stop adding time to the session
        pauseSession: (instanceId?: string) => {
          const now = Date.now();
          const id = instanceId || get().currentInstanceId;
          if (!id) return;
          let session = get().sessions[id];
          if (session && session.isActive) {
            const sessionTime = now - session.sessionStartTime;
            session.cumulativeTime += sessionTime;
            session.isActive = false;
            session.lastActivity = now;
            set((state) => ({
              sessions: { ...state.sessions, [id]: session }
            }));
          }
        },
        // Resume session – start adding time to the session
        resumeSession: (instanceId?: string) => {
        const now = Date.now();
        const id = instanceId || get().currentInstanceId;
        if (!id) return;
        let session = get().sessions[id];
        if (session && !session.isActive) {
          session.sessionStartTime = now;
          session.lastActivity = now;
          session.isActive = true;
          set((state) => ({
            sessions: { ...state.sessions, [id]: session }
          }));
        }
      },
        // Get total time + current session time for the instance
        getTimeSpent: (instanceId?: string): number => {
          const now = Date.now();

          const id = instanceId || get().currentInstanceId;
          if (!id) return 0;

          const { sessions } = get();
          const session = sessions[id];
          if (!session) return 0;
          let currentSessionTime = 0;
          if (session.isActive) {
            currentSessionTime = now - session.sessionStartTime;
          }
          const sec = Math.floor((session.cumulativeTime + currentSessionTime) / 1000); // in seconds
          console.log(`Time spent [getTimeSpent]: ${sec} seconds`);
          return sec;
        },
        // Reset after submit
        resetStartTime: (instanceId?: string) => {
          const now = Date.now();

          const id = instanceId || get().currentInstanceId;
          if (!id) return;

          console.log("Time spent [resetStartTime]")
          set((state) => ({
            sessions: {
              ...state.sessions,
              [id]: {
                sessionStartTime: now,
                cumulativeTime: 0,
                lastActivity: now,
                currentPageId: null,
                isActive: true,
              },
            },
          }));
        },
        // Update last activity for the current product (can be called periodically)
        updateCurrentActivity: () => {
          const { currentInstanceId, sessions } = get();
          if (currentInstanceId && sessions[currentInstanceId]) {
            const now = Date.now();
            const session = sessions[currentInstanceId];
            session.lastActivity = now;
            set((state) => ({
              sessions: { ...state.sessions, [currentInstanceId]: session },
            }));
          }
        },
        // Delete sessions that are inactive for more than 15 days (optional, for cleanup)
        expireSessions: () => {
          const now = Date.now();
          const { sessions } = get();
          const updatedSessions = { ...sessions };
          Object.keys(updatedSessions).forEach((productId) => {
            const session = updatedSessions[productId];
            if (now - session.lastActivity > SESSION_TIMEOUT) {
              delete updatedSessions[productId];
            }
          });
          console.log("Time spent [expireSessions]")
          set({ sessions: updatedSessions });
        },
      }),
      {
        name: 'time-spent-store',
        storage: createJSONStorage(() => localStorage),
      }
  )
);
